import React, { useRef, useEffect, useState, useCallback } from 'react';
import { Check, Lock } from 'lucide-react';

const BUBBLE_SIZE = 48;
const BUBBLE_SIZE_ACTIVE = 60;
const BUBBLE_GAP = 140;
const TRACK_HEIGHT = 240;
const CENTER_Y = TRACK_HEIGHT / 2;

const LearningJourneyTracker = ({ topics = [], onTopicClick, getTranslatedTitle, trackerBgImage, title = 'My Learning Path', showLocks = true }) => {
  const scrollRef = useRef(null);
  const activeRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const [tooltip, setTooltip] = useState(null);

  // Progress metrics
  const totalTopics = topics.length;
  const completedTopics = topics.filter(t => t.status === 'completed').length;
  const remainingTopics = totalTopics - completedTopics;

  // Find the first non-completed topic as the active one
  const activeIndex = topics.findIndex(t => t.status === 'in_progress')
    !== -1
      ? topics.findIndex(t => t.status === 'in_progress')
      : topics.findIndex(t => t.status === 'untapped' || !t.status);

  // Auto-center on active topic
  useEffect(() => {
    if (activeRef.current && scrollRef.current) {
      const container = scrollRef.current;
      const bubble = activeRef.current;
      const containerWidth = container.offsetWidth;
      const bubbleLeft = bubble.offsetLeft;
      const bubbleWidth = bubble.offsetWidth;
      container.scrollTo({
        left: bubbleLeft - containerWidth / 2 + bubbleWidth / 2,
        behavior: 'smooth',
      });
    }
  }, [activeIndex, topics.length]);

  // Drag-to-scroll handlers
  const handleMouseDown = useCallback((e) => {
    setIsDragging(true);
    setStartX(e.pageX - scrollRef.current.offsetLeft);
    setScrollLeft(scrollRef.current.scrollLeft);
  }, []);

  const handleMouseMove = useCallback((e) => {
    if (!isDragging) return;
    e.preventDefault();
    const x = e.pageX - scrollRef.current.offsetLeft;
    const walk = (x - startX) * 1.5;
    scrollRef.current.scrollLeft = scrollLeft - walk;
  }, [isDragging, startX, scrollLeft]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  useEffect(() => {
    document.addEventListener('mouseup', handleMouseUp);
    document.addEventListener('mousemove', handleMouseMove);
    return () => {
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('mousemove', handleMouseMove);
    };
  }, [handleMouseUp, handleMouseMove]);

  const getStatus = (topic, index) => {
    if (topic.status === 'completed') return 'completed';
    if (index === activeIndex) return 'active';
    return 'upcoming';
  };

  // Alternating: even indices (0,2,4…) = top, odd indices (1,3,5…) = bottom
  const isTop = (index) => index % 2 === 0;

  const getBubbleCenterX = (index) => {
    return 70 + index * BUBBLE_GAP;
  };

  // All bubble centers sit exactly on the training path line
  const getBubbleCenterY = () => CENTER_Y;

  const totalWidth = topics.length * BUBBLE_GAP + 80;

  return (
    <div
      className="w-full mb-4 rounded-xl overflow-hidden"
      style={{
        ...(trackerBgImage
          ? {
              backgroundImage: `url(${trackerBgImage})`,
              backgroundRepeat: 'repeat',
              backgroundSize: 'auto',
            }
          : {
              background: 'linear-gradient(to bottom, #87CEEB 0%, #87CEEB 80%, #4CAF50 80%, #4CAF50 100%)',
            }
        ),
      }}
    >
      {/* Header: Heading + Progress Box.
          Heading is hidden on mobile to save space; the summary box then centers on mobile
          and stays right-aligned on desktop/tablet (justify-center → md:justify-between). */}
      <div className="flex items-center justify-center md:justify-between px-3 md:px-4 pt-4 pb-2 min-w-0">
        <div className="hidden md:block">
          <h3 className="text-base font-bold text-white" style={{ textShadow: '0 1px 3px rgba(0,0,0,0.3)' }}>
            {title}
          </h3>
        </div>

        {/* Progress Summary Box */}
        <div className="learning-path-summary bg-white rounded-lg shadow-md px-3 md:px-4 py-2.5 flex-shrink-0 ml-0 md:ml-4 max-w-full">
          <div className="grid grid-cols-3 items-center min-w-0 w-full">
            <div className="learning-path-stat text-center">
              <p className="text-lg font-bold text-[#0056B3]">{totalTopics}</p>
              <p className="text-[10px] text-gray-500 font-medium leading-tight">Total Chapters</p>
            </div>
            <div className="learning-path-stat text-center border-x border-gray-200">
              <p className="text-lg font-bold text-emerald-500">{completedTopics}</p>
              <p className="text-[10px] text-gray-500 font-medium leading-tight">Completed</p>
            </div>
            <div className="learning-path-stat text-center">
              <p className="text-lg font-bold text-amber-500">{remainingTopics}</p>
              <p className="text-[10px] text-gray-500 font-medium leading-tight">Remaining</p>
            </div>
          </div>
        </div>
      </div>

      {/* Scrollable Track Area */}
      <div
        ref={scrollRef}
        onMouseDown={handleMouseDown}
        className="journey-scroll relative overflow-x-auto cursor-grab active:cursor-grabbing select-none max-w-full"
        style={{
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          WebkitOverflowScrolling: 'touch',
        }}
      >
        <div
          className="relative"
          style={{
            minWidth: `${totalWidth}px`,
            height: `${TRACK_HEIGHT}px`,
          }}
        >
          {/* Straight horizontal connector line */}
          <svg
            className="absolute top-0 left-0"
            width={totalWidth}
            height={TRACK_HEIGHT}
            style={{ pointerEvents: 'none' }}
          >
            {topics.length > 1 && (
              <line
                x1={getBubbleCenterX(0)}
                y1={CENTER_Y}
                x2={getBubbleCenterX(topics.length - 1)}
                y2={CENTER_Y}
                stroke="rgba(255,255,255,0.6)"
                strokeWidth={3}
                strokeLinecap="round"
              />
            )}
            {/* Completed overlay */}
            {topics.filter(t => t.status === 'completed').length > 0 && (
              <line
                x1={getBubbleCenterX(0)}
                y1={CENTER_Y}
                x2={getBubbleCenterX(topics.filter(t => t.status === 'completed').length - 1)}
                y2={CENTER_Y}
                stroke="#10B981"
                strokeWidth={3}
                strokeLinecap="round"
              />
            )}
          </svg>

          {/* Bubbles with alternating top/bottom positioning */}
          {topics.map((topic, index) => {
            const status = getStatus(topic, index);
            const isActive = status === 'active';
            const isCompleted = status === 'completed';
            const size = isActive ? BUBBLE_SIZE_ACTIVE : BUBBLE_SIZE;
            const topicName = getTranslatedTitle ? getTranslatedTitle(topic) : (topic.training_topic_name || topic.title);
            const progress = topic.status === 'completed' ? 100 : (topic.completion_percentage ? Math.round(topic.completion_percentage) : 0);
            const sections = topic.training_sections?.length || topic.sections?.length || 0;
            const top = isTop(index);
            const cx = getBubbleCenterX(index);
            // Top bubbles: bottom edge at CENTER_Y. Bottom bubbles: top edge at CENTER_Y.
            const bubbleTopPx = top ? (CENTER_Y - size) : CENTER_Y;
            const truncatedName = topicName && topicName.length > 30 ? topicName.substring(0, 28) + '…' : topicName;

            return (
              <div
                key={topic.training_topic_id || topic.id || index}
                ref={isActive ? activeRef : null}
                className="absolute"
                style={{
                  left: `${cx - BUBBLE_GAP / 2}px`,
                  top: 0,
                  width: `${BUBBLE_GAP}px`,
                  height: `${TRACK_HEIGHT}px`,
                }}
              >
                {/* Label — positioned absolutely so it never affects bubble placement */}
                <div
                  className="absolute left-0 right-0 flex flex-col items-center"
                  style={top
                    ? { bottom: `${TRACK_HEIGHT - bubbleTopPx + 8}px` }
                    : { top: `${bubbleTopPx + size + 4}px` }
                  }
                >
                  <span
                    className={`text-center max-w-[110px] ${
                      isActive ? 'text-xs font-bold text-white' : 'text-[11px] font-semibold text-white'
                    }`}
                    style={{ wordBreak: 'break-word', lineHeight: '1.35', textShadow: '0 1px 3px rgba(0,0,0,0.4)' }}
                  >
                    {truncatedName}
                  </span>
                  {isActive && (
                    <span className="text-[10px] mt-0.5 font-semibold text-white" style={{ textShadow: '0 1px 3px rgba(0,0,0,0.4)' }}>
                      Chapter {index + 1} of {topics.length}
                    </span>
                  )}
                </div>

                {/* Bubble — absolutely positioned, edge exactly on CENTER_Y */}
                <button
                  onClick={() => {
                    if (!isDragging) {
                      const contentId = topic.id || topic.content_id || topic.training_topic_id;
                      const trainingTopicId = topic.training_topic_id || topic.id;
                      onTopicClick(contentId, trainingTopicId, topic);
                    }
                  }}
                  onMouseEnter={(e) => {
                    const rect = e.currentTarget.getBoundingClientRect();
                    setTooltip({
                      index,
                      name: topicName,
                      status: isCompleted ? 'Completed' : isActive ? 'In Progress' : (topic.status === 'in_progress' ? 'In Progress' : 'Yet to Start'),
                      progress,
                      sections,
                      x: rect.left + rect.width / 2,
                      y: top ? rect.top : rect.bottom,
                      position: top ? 'above' : 'below',
                    });
                  }}
                  onMouseLeave={() => setTooltip(null)}
                  className={`
                    absolute z-10 flex items-center justify-center rounded-full border-[3px] transition-all duration-300
                    ${isCompleted
                      ? 'bg-emerald-500 border-emerald-400 text-white shadow-md'
                      : isActive
                        ? 'bg-[#0056B3] border-blue-400 text-white shadow-lg shadow-blue-200 ring-4 ring-blue-100'
                        : 'bg-gray-200 border-gray-300 text-gray-400'
                    }
                  `}
                  style={{
                    width: `${size}px`,
                    height: `${size}px`,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    top: `${bubbleTopPx}px`,
                  }}
                  aria-label={topicName}
                >
                  {isCompleted ? (
                    <Check className="w-5 h-5" strokeWidth={3} />
                  ) : isActive ? (
                    <span className="text-xs font-bold">{progress}%</span>
                  ) : showLocks ? (
                    <Lock className="w-4 h-4" />
                  ) : (
                    <span className="text-xs font-bold text-gray-500">{index + 1}</span>
                  )}
                  {isActive && (
                    <span className="absolute inset-0 rounded-full animate-ping bg-blue-400 opacity-20" />
                  )}
                </button>
              </div>
            );
          })}
        </div>
      </div>

      {/* Tooltip (portal-style, fixed position) */}
      {tooltip && (
        <div
          className="fixed z-50 pointer-events-none"
          style={{
            left: `${tooltip.x}px`,
            top: tooltip.position === 'above' ? `${tooltip.y - 8}px` : `${tooltip.y + 8}px`,
            transform: tooltip.position === 'above' ? 'translate(-50%, -100%)' : 'translate(-50%, 0)',
          }}
        >
          <div className="bg-gray-900 text-white rounded-lg px-3 py-2 shadow-xl text-xs max-w-[min(17rem,calc(100vw-1rem))] whitespace-normal">
            <p className="font-semibold mb-1">{tooltip.name}</p>
            <p className="text-gray-300">Status: {tooltip.status}</p>
            {tooltip.sections > 0 && (
              <p className="text-gray-300">Subchapters: {tooltip.sections}</p>
            )}
            {tooltip.progress > 0 && (
              <p className="text-gray-300">Progress: {tooltip.progress}%</p>
            )}
            {/* Arrow */}
            <div className="absolute left-1/2 -translate-x-1/2 top-full w-0 h-0 border-l-[6px] border-l-transparent border-r-[6px] border-r-transparent border-t-[6px] border-t-gray-900" />
          </div>
        </div>
      )}
    </div>
  );
};

export default LearningJourneyTracker;
